// apps/web/src/services/billing.ts
//
// Billing v2 — Product catalog, subscriptions, batch invoice generation.
// All write paths go through edge functions to enforce RLS / approval.

import { supabase } from './supabase';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface BillingProduct {
  id: string;
  code: string;
  name: string;
  description: string;
  default_quantity: number;
  default_unit_price: number;
  currency: string;
  is_service: boolean;
  unit_measure: string;
  is_active: boolean;
  sort_order: number;
  patrol_monthly_free: number | null;
  max_sub_accounts: number | null;
  created_at: string;
  updated_at: string;
}

export type BillingProductInput = Omit<BillingProduct, 'id' | 'created_at' | 'updated_at'>;

export type BillingInterval   = 'monthly' | 'quarterly' | 'yearly';
export type SubscriptionStatus = 'active' | 'paused' | 'cancelled';
export type InvoiceMode       = 'consolidated' | 'per_site';

export interface BillingSubscription {
  id: string;
  account_id: string;
  site_id: string | null;
  product_id: string;
  quantity: number;
  unit_price_override: number | null;
  description_override: string | null;
  billing_interval: BillingInterval;
  billing_day: number;
  start_date: string;
  end_date: string | null;
  next_billing_date: string;
  last_billed_period: string | null;
  auto_emit_fel: boolean;
  auto_charge: boolean;
  status: SubscriptionStatus;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export type BillingSubscriptionInput = Omit<
  BillingSubscription,
  'id' | 'created_at' | 'updated_at' | 'last_billed_period'
>;

export interface BillingCronConfig {
  enabled: boolean;
  schedule_mode: 'manual' | 'first_business_day' | 'fixed_day';
  day_of_month: number;
  hour_local: number;
  timezone: string;
  auto_emit_fel_after_approval: boolean;
  notify_admins: boolean;
}

export interface InvoiceBatch {
  id: string;
  origin: 'manual' | 'cron';
  billing_period: string;
  status: 'pending' | 'running' | 'completed' | 'completed_with_errors' | 'failed';
  total_items: number;
  success_count: number;
  failed_count: number;
  skipped_count: number;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface InvoiceBatchItem {
  id: string;
  batch_id: string;
  account_id: string;
  site_id: string | null;
  subscription_id: string | null;
  invoice_id: string | null;
  status: 'pending' | 'success' | 'failed' | 'skipped';
  error_message: string | null;
  amount: number | null;
  created_at: string;
  processed_at: string | null;
}

// ─── Products ───────────────────────────────────────────────────────────────

export async function listBillingProducts(): Promise<BillingProduct[]> {
  const { data, error } = await supabase
    .from('billing_products')
    .select('*')
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });
  if (error) throw error;
  return (data ?? []) as BillingProduct[];
}

export async function createBillingProduct(p: BillingProductInput): Promise<BillingProduct> {
  const { data, error } = await supabase.from('billing_products').insert(p).select().single();
  if (error) throw error;
  return data as BillingProduct;
}

export async function updateBillingProduct(id: string, p: Partial<BillingProductInput>): Promise<BillingProduct> {
  const { data, error } = await supabase
    .from('billing_products').update(p).eq('id', id).select().single();
  if (error) throw error;
  return data as BillingProduct;
}

export async function deleteBillingProduct(id: string): Promise<void> {
  const { error } = await supabase.from('billing_products').delete().eq('id', id);
  if (error) throw error;
}

// ─── Subscriptions ──────────────────────────────────────────────────────────

export async function listSubscriptions(filter: { accountId?: string; dueOnly?: boolean; period?: string } = {}): Promise<BillingSubscription[]> {
  let q = supabase.from('billing_subscriptions').select('*').order('account_id', { ascending: true });
  if (filter.accountId) q = q.eq('account_id', filter.accountId);
  if (filter.dueOnly) q = q.eq('status', 'active');
  if (filter.dueOnly && filter.period) q = q.lte('next_billing_date', filter.period);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as BillingSubscription[];
}

export async function createSubscription(s: BillingSubscriptionInput): Promise<BillingSubscription> {
  const { data, error } = await supabase.from('billing_subscriptions').insert(s).select().single();
  if (error) throw error;
  return data as BillingSubscription;
}

export async function updateSubscription(id: string, s: Partial<BillingSubscriptionInput>): Promise<BillingSubscription> {
  const { data, error } = await supabase
    .from('billing_subscriptions').update(s).eq('id', id).select().single();
  if (error) throw error;
  return data as BillingSubscription;
}

export async function deleteSubscription(id: string): Promise<void> {
  const { error } = await supabase.from('billing_subscriptions').delete().eq('id', id);
  if (error) throw error;
}

// ─── Manual invoice creation ─────────────────────────────────────────────────

export interface CreateInvoiceParams {
  account_id:  string;
  amount:      number;
  currency?:   string;          // default 'GTQ'
  description: string;
  due_date?:   string | null;   // YYYY-MM-DD
  notes?:      string | null;
  status?:     'draft' | 'issued';   // default 'draft'
}

export async function createManualInvoice(params: CreateInvoiceParams): Promise<{ id: string; invoice_number: string }> {
  const status = params.status ?? 'draft';
  const { data, error } = await supabase
    .from('invoices')
    .insert({
      account_id:  params.account_id,
      amount:      params.amount,
      currency:    params.currency ?? 'GTQ',
      description: params.description,
      due_date:    params.due_date ?? null,
      notes:       params.notes ?? null,
      status,
      issued_at:   status === 'issued' ? new Date().toISOString() : null,
    } as never)
    .select('id, invoice_number')
    .single();
  if (error) throw error;
  return data as { id: string; invoice_number: string };
}

// ─── Suspension check (admin manual trigger) ────────────────────────────────
export async function runSuspensionCheck(): Promise<void> {
  const { error: e1 } = await supabase.rpc('fn_mark_overdue_invoices' as never);
  if (e1) throw e1;
  const { error: e2 } = await supabase.rpc('fn_auto_suspend_overdue_accounts' as never);
  if (e2) throw e2;
}

// ─── Batch generation ───────────────────────────────────────────────────────
export interface BatchGenerateParams {
  billing_period?: string;             // 'YYYY-MM-01'
  subscription_ids?: string[];
  mode?: 'due' | 'manual';
  auto_emit_fel?: boolean;
  notes?: string;
}

export interface BatchGenerateResult {
  batch_id: string;
  total_items: number;
  success_count: number;
  failed_count: number;
  skipped_count: number;
  status: InvoiceBatch['status'];
}

export async function runBatch(params: BatchGenerateParams): Promise<BatchGenerateResult> {
  const { data, error } = await supabase.functions.invoke('billing-batch-generate', {
    body: params,
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data as BatchGenerateResult;
}

export async function listBatches(limit = 50): Promise<InvoiceBatch[]> {
  const { data, error } = await supabase
    .from('invoice_batches').select('*')
    .order('created_at', { ascending: false }).limit(limit);
  if (error) throw error;
  return (data ?? []) as InvoiceBatch[];
}

export async function listBatchItems(batchId: string): Promise<InvoiceBatchItem[]> {
  const { data, error } = await supabase
    .from('invoice_batch_items').select('*').eq('batch_id', batchId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as InvoiceBatchItem[];
}

// ─── Approval ───────────────────────────────────────────────────────────────

export async function approveInvoice(invoiceId: string, emitFel = false): Promise<{
  approved: true;
  fel?: { uuid: string; pdf_url: string };
  fel_error?: { error: string; message?: string; all_errors?: string[] };
}> {
  // Use fetch directly so we can read the response body on errors
  const session = (await supabase.auth.getSession()).data.session;
  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invoice-approve`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token ?? ''}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ invoice_id: invoiceId, emit_fel: emitFel }),
    },
  );
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(payload?.error ?? `Error ${res.status} al aprobar factura`);
  if (payload?.error) throw new Error(payload.error);
  return payload;
}

/**
 * Voids a never-paid, never-FEL-certified invoice created by a billing bug
 * or a client backing out before paying (plan_activation), or an erroneous
 * patrol dispatch charge (patrol_dispatch) — e.g. process-alert billing an
 * account that didn't actually have an active subscription. For a
 * plan_activation tied to a fresh account activation, also reverts the
 * subscription out of the PAST_DUE limbo it was left in. A FEL-certified
 * invoice must go through the formal SAT cancellation instead
 * (InvoiceFelActions "Anular").
 */
export async function voidPendingPlanInvoice(invoiceId: string): Promise<void> {
  const { data: invoice, error: fetchErr } = await supabase
    .from('invoices')
    .select('id, account_id, status, invoice_type, fel_uuid, notes')
    .eq('id', invoiceId)
    .single();
  if (fetchErr) throw fetchErr;
  if (!['draft', 'issued', 'overdue'].includes(invoice.status)) {
    throw new Error(`Esta factura no se puede cancelar (estado: ${invoice.status}).`);
  }
  if (invoice.fel_uuid) {
    throw new Error('Esta factura ya fue certificada con SAT — usa el botón "Anular" (requiere proceso SAT).');
  }
  if (!['plan_activation', 'patrol_dispatch'].includes(invoice.invoice_type ?? '')) {
    throw new Error('Solo se pueden cancelar así facturas de activación de plan o despacho de patrulla.');
  }

  let notes: { action?: string } | null = null;
  try { notes = invoice.notes ? JSON.parse(invoice.notes) : null; } catch { /* plain-text notes */ }
  if (invoice.invoice_type === 'plan_activation' && notes?.action !== 'upgrade') {
    const { data: sub } = await supabase
      .from('subscriptions')
      .select('id, status')
      .eq('account_id', invoice.account_id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (sub && sub.status === 'PAST_DUE') {
      await supabase.from('subscriptions').update({ status: 'EXPIRED' }).eq('id', sub.id);
    }
  }

  const { error: voidErr } = await supabase
    .from('invoices')
    .update({ status: 'void', notes: `Cancelada por el operador (${new Date().toISOString()}).` })
    .eq('id', invoiceId);
  if (voidErr) throw voidErr;
}

// ─── Billing settings (cron + toggles) ──────────────────────────────────────

export async function getBillingCronConfig(): Promise<BillingCronConfig> {
  const { data, error } = await supabase
    .from('system_settings').select('value').eq('key', 'billing_cron').maybeSingle();
  if (error) throw error;
  const value = (data?.value ?? {}) as Partial<BillingCronConfig>;
  return {
    enabled: value.enabled ?? false,
    schedule_mode: value.schedule_mode ?? 'first_business_day',
    day_of_month: value.day_of_month ?? 1,
    hour_local: value.hour_local ?? 7,
    timezone: value.timezone ?? 'America/Guatemala',
    auto_emit_fel_after_approval: value.auto_emit_fel_after_approval ?? false,
    notify_admins: value.notify_admins ?? true,
  };
}

export async function setBillingCronConfig(cfg: BillingCronConfig): Promise<void> {
  const { error } = await supabase
    .from('system_settings').upsert({ key: 'billing_cron', value: cfg });
  if (error) throw error;
}

export async function getBillingFlags(): Promise<{
  block_unvalidated_nit: boolean;
  require_approval: boolean;
}> {
  const { data, error } = await supabase
    .from('system_settings').select('key,value')
    .in('key', ['billing_block_unvalidated_nit', 'billing_require_approval']);
  if (error) throw error;
  const map = new Map<string, unknown>();
  for (const row of data ?? []) map.set(row.key, row.value);
  return {
    block_unvalidated_nit: map.get('billing_block_unvalidated_nit') === true,
    require_approval:      map.get('billing_require_approval') === true,
  };
}

export async function setBillingFlag(key: 'billing_block_unvalidated_nit' | 'billing_require_approval', value: boolean): Promise<void> {
  const { error } = await supabase
    .from('system_settings').upsert({ key, value });
  if (error) throw error;
}

// ─── Suspension policy ───────────────────────────────────────────────────────

export interface SuspensionPolicy {
  overdue_count: number; // suspend when >= N invoices are overdue
  overdue_days:  number; // suspend when oldest overdue invoice is >= X days old
}

export async function getSuspensionPolicy(): Promise<SuspensionPolicy> {
  const { data, error } = await supabase
    .from('system_settings').select('value').eq('key', 'billing_suspension_policy').maybeSingle();
  if (error) throw error;
  const v = (data?.value ?? {}) as Partial<SuspensionPolicy>;
  return {
    overdue_count: v.overdue_count ?? 2,
    overdue_days:  v.overdue_days  ?? 60,
  };
}

export async function setSuspensionPolicy(policy: SuspensionPolicy): Promise<void> {
  const { error } = await supabase
    .from('system_settings').upsert({ key: 'billing_suspension_policy', value: policy });
  if (error) throw error;
}

// ─── Helper: render description preview ─────────────────────────────────────
export function renderDescription(template: string, ctx: {
  period?: Date;
  siteName?: string | null;
  accountName?: string | null;
  nit?: string | null;
}): string {
  const months = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const d = ctx.period ?? new Date();
  const month = months[d.getMonth()];
  const year  = d.getFullYear();
  return template
    .replaceAll('{month}',       month)
    .replaceAll('{year}',        String(year))
    .replaceAll('{period}',      `${month} ${year}`)
    .replaceAll('{site_name}',   ctx.siteName ?? '')
    .replaceAll('{account_name}', ctx.accountName ?? '')
    .replaceAll('{nit}',         ctx.nit ?? '');
}
